import {
  localDateSchema,
  blockPhaseSchema,
  musclePrioritiesSchema,
  sessionExerciseSchema,
  setPrescriptionSchema,
  muscleSchema,
  progressionStateSchema,
  progressionStrategySchema,
  trainingBlockSchema,
  trainingDecisionSchema,
  sessionPlanSchema,
  volumeTargetsSchema,
  type SessionExercise,
  type SessionStatus,
  type SetPrescription,
  type TrainingDecision,
} from '@cadencia/contracts';
import { z } from 'zod';
import {
  baseSetsOf,
  accumulationWeeksLeftOf,
  chooseTechnique,
  expandSets,
  findExercise,
  findSubstitutions,
  roundKg,
  evaluateAfterFor,
} from '@cadencia/domain';
import { diaLocalDe, todayLocal } from '../../lib/dates.js';
import { rehacerSemanaPreparada } from './semanaPreparada.js';
import { logger } from '../../lib/logger.js';
import { db } from '../db.js';
import { nowIso } from '../ids.js';
import { getProgressionState } from '../repositories/progressionRepo.js';
import { getDefaultGym, listEquipment } from '../repositories/gymRepo.js';
import { getWorkoutBySession, listPerformedSets } from '../repositories/workoutRepo.js';
import {
  closeBlock,
  createBlockForPhase,
  getActiveBlock,
  getSessionDetail,
  listSessionsOfWeek,
  listWeeks,
  updateSession,
} from '../repositories/trainingRepo.js';
import {
  newSyncColumns,
  putNewRows,
  putRow,
  softDeleteRow,
  type WriteContext,
} from '../writes.js';

/**
 * Aplicacion de decisiones: el paso que faltaba.
 *
 * Hasta aqui siete motores proponian y ninguno actuaba. Aprobar cambiaba
 * `approval_status` y el plan se quedaba igual.
 *
 * Las tres reglas vienen del §10 y no se negocian:
 *
 * 1. Un cambio MENOR se aplica sin preguntar, con su «¿Por qué?» disponible.
 * 2. Un cambio RELEVANTE solo se aplica tras la aprobacion del usuario.
 * 3. Un cambio CADUCADO no se aplica NUNCA: «el plan continúa sin aplicarlos».
 *
 * La tercera es la que hay que defender con cuidado. Una propuesta de hace diez
 * dias se calculo con datos de hace diez dias; aplicarla tarde es aplicar una
 * recomendacion que ya no describe la situacion.
 */

/** Estados desde los que una decision puede llevarse al plan. */
const APLICABLES: ReadonlySet<string> = new Set(['not_required', 'approved']);

export interface ApplyOutcome {
  applied: TrainingDecision[];
  /** Lo que no se aplico y por que. No es un error: casi todo es «todavia no». */
  skipped: { id: string; decisionType: string; reason: string }[];
}

interface Movimiento {
  sessionId: string;
  from: string;
  to: string;
}

/**
 * Lleva al plan todo lo que ya puede aplicarse.
 *
 * Es idempotente por la marca `appliedAt`, no por confiar en que cada aplicador
 * se pueda repetir sin consecuencias. La revision semanal corre cada vez que se
 * abre el panel de propuestas, asi que esta funcion se ejecuta a menudo.
 */
export async function applyDecisions(ctx: WriteContext, now = nowIso()): Promise<ApplyOutcome> {
  const filas = (await db.trainingDecisions.where('userId').equals(ctx.userId).toArray())
    .filter(
      (fila) =>
        fila.deletedAt === null &&
        fila.appliedAt === null &&
        APLICABLES.has(fila.approvalStatus),
    )
    // En orden de decision: si dos tocan la misma sesion, gana la mas reciente.
    .sort((a, b) => a.decidedAt.localeCompare(b.decidedAt));

  const applied: TrainingDecision[] = [];
  const skipped: ApplyOutcome['skipped'] = [];

  for (const fila of filas) {
    const motivo = await aplicarUna(ctx, fila);

    if (motivo !== null) {
      skipped.push({ id: fila.id, decisionType: fila.decisionType, reason: motivo });
      continue;
    }

    /*
     * `evaluateAfter` se fija AL APLICAR, no al decidir. Lo que se juzga es la
     * respuesta a un cambio que de verdad entró en el plan, y la exposición
     * cuenta desde que entró: una propuesta que esperó tres semanas en el panel
     * no ha tenido tres semanas de efecto.
     *
     * Hasta el 22/09/2026 esta columna se escribía SIEMPRE en `null`
     * (`decisionsRepo.ts`), igual que `outcome`: el motor registraba lo que
     * decidía y nunca comprobaba si había servido.
     */
    const marcada = await putRow(
      ctx,
      'trainingDecisions',
      trainingDecisionSchema.parse({
        ...fila,
        appliedAt: now,
        evaluateAfter: evaluateAfterFor(fila.decisionType, diaLocalDe(now)),
      }),
    );
    applied.push(marcada);
  }

  // Una semana preparada de antemano se construyó con el volumen y las
  // prioridades que el bloque tenía entonces. Si acaba de cambiar alguno y nadie
  // ha entrenado todavía en ella, se rehace; si no, el cambio solo entraría en
  // la semana siguiente a esa (decisión del 13/09/2026, `semanaPreparada.ts`).
  if (applied.some((fila) => fila.decisionType === 'volume_change' || fila.decisionType === 'priority_change')) {
    await rehacerSemanaPreparada(ctx, diaLocalDe(now), 'rebuild');
  }

  if (applied.length > 0 || skipped.length > 0) {
    logger.info('engine', 'DECISIONS_APPLIED', 'Decisiones llevadas al plan', {
      aplicadas: applied.length,
      omitidas: skipped.length,
    });
  }

  return { applied, skipped };
}

/**
 * Aplica una decision. Devuelve `null` si se aplico, o el motivo si no.
 *
 * Lo que no tiene aplicador NO se marca como aplicado. Es la diferencia entre
 * «esto todavia no lo se hacer» y «esto ya esta hecho», y en un registro de
 * decisiones de salud no pueden parecer lo mismo.
 */
async function aplicarUna(ctx: WriteContext, fila: TrainingDecision): Promise<string | null> {
  switch (fila.decisionType) {
    case 'week_reorganization':
      return aplicarReorganizacion(ctx, fila);
    case 'stall_response':
      return aplicarEstancamiento(ctx, fila);
    case 'volume_change':
      return aplicarVolumen(ctx, fila);
    case 'phase_change':
      return aplicarCambioDeFase(ctx, fila);
    case 'deload':
      return aplicarDescarga(ctx, fila);
    case 'exercise_substitution':
      return aplicarSustitucion(ctx, fila);
    case 'priority_change':
      return aplicarPrioridades(ctx, fila);
    // Ninguna de las dos toca el plan al aplicarse. `plan_kept` consiste en no
    // tocarlo, y `block_start` se registra cuando el bloque YA se ha creado.
    // Sin estas dos lineas se quedarian para siempre como «sin aplicador», y
    // cada revision las volveria a intentar.
    case 'plan_kept':
    case 'block_start':
      return null;
    default:
      return `sin aplicador para ${fila.decisionType}`;
  }
}

/** Mueve las sesiones reubicadas a su nueva fecha. */
async function aplicarReorganizacion(
  ctx: WriteContext,
  fila: TrainingDecision,
): Promise<string | null> {
  const moves = (fila.inputs as { moves?: Movimiento[] }).moves ?? [];
  if (moves.length === 0) {
    // Aceptar menos volumen no es una accion sobre el plan: no hay nada que
    // mover. Se marca como aplicada igualmente porque la decision esta tomada.
    return null;
  }

  let movidas = 0;
  for (const move of moves) {
    const sesion = await db.trainingSessions.get(move.sessionId);
    if (!sesion || sesion.deletedAt !== null) continue;
    // Si alguien la entreno entre la propuesta y la aprobacion, no se toca.
    if (sesion.status === 'completed' || sesion.status === 'in_progress') continue;

    await updateSession(ctx, sesion, {
      scheduledDate: move.to,
      rescheduledFrom: move.from,
      // Vuelve a `planned`, no a `rescheduled`: el usuario tiene que poder
      // entrenarla, y `getNextSession` solo mira lo planificado y lo empezado.
      // `rescheduledFrom` es lo que deja constancia de que se movio.
      status: 'planned',
    });
    movidas += 1;
  }

  return movidas > 0 ? null : 'ninguna de las sesiones sigue siendo movible';
}

/**
 * Lo que se acepta como propuesta guardada en `inputs`.
 *
 * Deliberadamente estrecho: solo lo que cambia el bloque. El `rationale` y los
 * recortes se guardan tambien para poder enseñarlos, pero no entran aqui
 * porque no se aplican.
 */
const aiBlockProposalInputSchema = z.object({
  weeks: z.number().int().min(1).max(16),
  targets: volumeTargetsSchema,
  /**
   * La estructura de sesiones, si la propuesta la traia y el dominio la acepto.
   *
   * `.nullish()` y no `.optional()`: hay dos casos distintos que producen
   * ausencia y los dos son legitimos. Una propuesta guardada ANTES del
   * 22/09/2026 no tiene la clave; una posterior cuya estructura se rechazo la
   * tiene en `null`. Las dos significan lo mismo para quien aplica —el bloque
   * sale de la plantilla— y ninguna puede tumbar la aplicacion de las semanas y
   * el volumen, que si son suyos.
   */
  sessionPlan: sessionPlanSchema.nullish(),
});

/**
 * Cierra el bloque agotado y abre el de la fase aprobada (§2.3).
 *
 * El orden importa: primero se CREA el nuevo y despues se cierra el viejo. Al
 * reves, un fallo al construir el bloque nuevo —un gimnasio sin equipamiento,
 * por ejemplo— dejaria al usuario sin ninguno y sin forma de salir de ahi.
 */
async function aplicarCambioDeFase(
  ctx: WriteContext,
  fila: TrainingDecision,
): Promise<string | null> {
  const fase = blockPhaseSchema.safeParse(fila.newValue);
  if (!fase.success) return 'el valor propuesto no es una fase de bloque';

  const actual = await getActiveBlock(ctx.userId);
  if (!actual) return 'no hay bloque activo que sustituir';
  if (actual.phase === fase.data) return 'el bloque activo ya es de esa fase';

  /**
   * Cuando empieza el bloque nuevo.
   *
   * Por defecto, el dia en que se decidio: encadenar sin hueco es lo correcto,
   * porque un bloque que empieza tres semanas despues del anterior no es
   * continuidad. Pero el usuario puede haber elegido otra fecha antes de
   * aprobar —«termino este, me voy dos semanas, empiezo la definicion al
   * volver»—, y entonces manda la suya.
   *
   * Se valida la FORMA aqui, no solo al guardarla: entre elegirla y aprobar
   * pueden pasar hasta 7 dias y la fila viaja por sincronizacion.
   *
   * El mismo `inicio` cierra el bloque viejo, asi que retrasar el nuevo alarga
   * el anterior hasta ese dia en vez de dejar un hueco sin plan.
   */
  const elegida = localDateSchema.safeParse(
    (fila.inputs as { startDate?: unknown }).startDate,
  );
  // `decidedAt` es un INSTANTE en UTC y `startDate` un dia del calendario del
  // usuario: aprobar a las 00:30 empezaba el bloque —y cerraba el anterior— el
  // dia de ayer. Misma clase que BUG-001, sitio que la auditoria no enumero.
  const inicio = elegida.success ? elegida.data : diaLocalDe(fila.decidedAt);

  // La propuesta de la IA, si el usuario la pidio antes de aprobar. Viene ya
  // recortada por `validateBlockProposal` —esa es la condicion para que se
  // guardara— y se vuelve a validar la FORMA aqui: entre que se guardo y que
  // se aprueba pueden pasar hasta 7 dias, y la fila viaja por sincronizacion.
  const propuesta = aiBlockProposalInputSchema.safeParse(
    (fila.inputs as { aiBlockProposal?: unknown }).aiBlockProposal,
  );

  // Si la semana siguiente se preparó al cerrar la actual, pertenece al bloque
  // viejo: se retira antes, o el bloque nuevo pondría sus sesiones encima y
  // habría dos planes el mismo día. Con el bloque viejo como argumento, porque
  // en cuanto se crea el nuevo deja de ser el activo.
  await rehacerSemanaPreparada(ctx, todayLocal(), 'discard', actual);

  await createBlockForPhase(ctx, fase.data, {
    startDate: inicio,
    // Las prioridades declaradas se conservan al cambiar de fase: son del
    // usuario, no del bloque que termina.
    musclePriorities: actual.musclePriorities,
    ...(propuesta.success
      ? {
          weeks: propuesta.data.weeks,
          volumeTargets: propuesta.data.targets,
          ...(propuesta.data.sessionPlan ? { sessionPlan: propuesta.data.sessionPlan } : {}),
        }
      : {}),
  });
  await closeBlock(ctx, actual, inicio);
  return null;
}

/**
 * Fija el objetivo de volumen del bloque para ese musculo (§4.3).
 *
 * No reescribe las series de la semana en curso. Es deliberado: cambiar sobre
 * la marcha lo que ya esta prescrito significaria que el usuario abre la
 * aplicacion a mitad de semana y se encuentra otra sesion de la que dejo. El
 * objetivo entra en vigor cuando se materializa la semana siguiente, que es
 * donde `ensureWeekMaterialized` lo lee.
 */
async function aplicarVolumen(
  ctx: WriteContext,
  fila: TrainingDecision,
): Promise<string | null> {
  const muscle = muscleSchema.safeParse((fila.inputs as { muscle?: unknown }).muscle);
  if (!muscle.success) return 'la propuesta no dice que musculo';

  const objetivo = fila.newValue;
  if (typeof objetivo !== 'number' || !Number.isFinite(objetivo)) {
    return 'el valor propuesto no es un numero de series';
  }

  const block = await getActiveBlock(ctx.userId);
  if (!block) return 'no hay bloque activo al que fijarle el objetivo';

  const targets = volumeTargetsSchema.safeParse({
    ...block.volumeTargets,
    [muscle.data]: objetivo,
  });
  // El contrato acota el valor (0-40). Si no pasa, se omite en lugar de
  // guardarlo: un objetivo corrupto acabaria convertido en series prescritas.
  if (!targets.success) return 'el objetivo resultante esta fuera de rango';

  if (block.volumeTargets[muscle.data] === objetivo) return null; // ya estaba

  await putRow(
    ctx,
    'trainingBlocks',
    trainingBlockSchema.parse({ ...block, volumeTargets: targets.data }),
  );
  return null;
}

/**
 * De la escalera del §9, solo el escalon 2 tiene aplicacion mecanica hoy.
 *
 * Los demas se omiten con su motivo en lugar de marcarse como aplicados: la
 * reduccion tecnica y la sustitucion tocan series ya prescritas, y la revision
 * estructural no es una accion automatica, es un aviso de que hay que mirar
 * la descarga o la estructura del bloque.
 */
async function aplicarEstancamiento(
  ctx: WriteContext,
  fila: TrainingDecision,
): Promise<string | null> {
  const entradas = fila.inputs as {
    exerciseSlug?: string;
    equipmentId?: string | null;
    action?: string;
    loadFactor?: number | null;
  };

  if (typeof entradas.exerciseSlug !== 'string') return 'la propuesta no dice que ejercicio';

  // Escalon 3: bajar la carga y reconstruir. La prescripcion no lleva carga
  // —la calcula el motor a partir del e1RM—, asi que la palanca real es el
  // propio e1RM estimado. Tocar ahi baja lo que se propone sin inventar un
  // campo nuevo ni reescribir series.
  if (entradas.action === 'technical_deload') {
    const factor = typeof entradas.loadFactor === 'number' ? entradas.loadFactor : 0.9;
    const estadoTecnico = await getProgressionState(
      ctx.userId,
      entradas.exerciseSlug,
      entradas.equipmentId ?? null,
    );
    if (!estadoTecnico) return 'ese ejercicio todavia no tiene estado de progresion';
    if (estadoTecnico.currentE1rmKg === null) {
      return 'todavia no hay maximo estimado que reducir';
    }

    await putRow(
      ctx,
      'progressionState',
      progressionStateSchema.parse({
        ...estadoTecnico,
        currentE1rmKg: roundKg(estadoTecnico.currentE1rmKg * factor),
        // Se reinicia el contador: la respuesta ya se ha dado, y dejarlo alto
        // haria que la proxima revision saltara directa al escalon siguiente.
        stallCounter: 0,
      }),
    );
    logger.info('engine', 'TECHNICAL_DELOAD_APPLIED', 'Reduccion tecnica aplicada', {
      exerciseSlug: entradas.exerciseSlug,
      factor,
    });
    return null;
  }

  // Escalon 4: sustituir por una variante del mismo patron. La eleccion la hace
  // el motor de sustituciones con el equipamiento real del gimnasio; aqui solo
  // se lleva al plan.
  if (entradas.action === 'substitute_exercise') {
    return sustituirPorEstancamiento(ctx, fila, entradas.exerciseSlug);
  }

  if (entradas.action !== 'change_strategy') {
    return `la accion «${entradas.action ?? 'desconocida'}» todavia no se aplica sola`;
  }

  // Se valida contra el propio esquema en lugar de con una lista repetida
  // aqui: una cuarta copia de las cinco estrategias acabaria divergiendo.
  const estrategia = progressionStrategySchema.safeParse(fila.newValue);
  if (!estrategia.success) return 'el valor propuesto no es una estrategia de progresion';

  const estado = await getProgressionState(
    ctx.userId,
    entradas.exerciseSlug,
    entradas.equipmentId ?? null,
  );
  if (!estado) return 'ese ejercicio todavia no tiene estado de progresion';
  if (estado.strategy === estrategia.data) return null; // ya estaba

  await putRow(
    ctx,
    'progressionState',
    progressionStateSchema.parse({ ...estado, strategy: estrategia.data }),
  );
  return null;
}

// ---------------------------------------------------------------------------
// Descarga
// ---------------------------------------------------------------------------

/** Cuanto volumen se quita en cada tipo de descarga (docs/TRAINING_ENGINE.md §5.2). */
const RECORTE_DE_DESCARGA: Record<string, number> = { partial: 0.2, full: 0.45 };

/** Cuanto sube el RIR objetivo: alejarse del fallo es la otra mitad de descargar. */
const RIR_EXTRA_DESCARGA = 1;

/**
 * Aplica la descarga a las sesiones que TODAVIA no se han hecho.
 *
 * Solo toca sesiones `planned`. Una sesion en curso o terminada no se reescribe:
 * el historial es la base de todas las decisiones posteriores, y cambiarlo a
 * posteriori invalidaria los analisis que ya se hicieron sobre el.
 */
async function aplicarDescarga(ctx: WriteContext, fila: TrainingDecision): Promise<string | null> {
  const accion = typeof fila.newValue === 'string' ? fila.newValue : null;
  const recorte = accion === null ? undefined : RECORTE_DE_DESCARGA[accion];
  if (recorte === undefined) return `descarga «${String(accion)}» desconocida`;

  const bloque = await getActiveBlock(ctx.userId);
  if (!bloque) return 'no hay bloque activo';

  const semanas = await listWeeks(bloque.id);
  const semana = semanas.find((s) => s.status === 'active') ?? semanas[0];
  if (!semana) return 'el bloque no tiene semanas';

  const sesiones = (await listSessionsOfWeek(semana.id)).filter((s) => s.status === 'planned');
  if (sesiones.length === 0) return 'no quedan sesiones por hacer en la semana en curso';

  let seriesQuitadas = 0;

  for (const sesion of sesiones) {
    const detalle = await getSessionDetail(sesion.id);
    if (!detalle) continue;

    for (const item of detalle.exercises) {
      const efectivas = item.prescriptions.filter((p) => p.setType === 'effective');
      // Nunca se baja de una serie: descargar no es borrar el ejercicio.
      const objetivo = Math.max(1, Math.round(efectivas.length * (1 - recorte)));

      for (const prescripcion of efectivas.slice(objetivo)) {
        await softDeleteRow(ctx, 'setPrescriptions', prescripcion.id);
        seriesQuitadas += 1;
      }

      for (const prescripcion of efectivas.slice(0, objetivo)) {
        const nuevoRir = Math.min(6, prescripcion.targetRir + RIR_EXTRA_DESCARGA);
        if (nuevoRir === prescripcion.targetRir) continue;
        await putRow(
          ctx,
          'setPrescriptions',
          setPrescriptionSchema.parse({ ...prescripcion, targetRir: nuevoRir }),
        );
      }
    }
  }

  logger.info('engine', 'DELOAD_APPLIED', 'Descarga aplicada a la semana en curso', {
    accion,
    sesiones: sesiones.length,
    seriesQuitadas,
  });
  return null;
}

// ---------------------------------------------------------------------------
// Sustitucion de ejercicio
// ---------------------------------------------------------------------------

/**
 * En que sesiones puede entrar una sustitucion.
 *
 * `planned` siempre: es el caso normal, decidido desde la previsualizacion o
 * por el escalon 4 del estancamiento.
 *
 * `in_progress` **solo si la propuesta señala un hueco concreto**, y esa
 * condicion es la que hace que la regla sea segura. El motivo por el que
 * alguien cambia un ejercicio a mitad de sesion es que no lo puede hacer AHORA
 * —la maquina esta ocupada, el banco roto, la sala llena—, y eso es un hueco,
 * no un ejercicio: una sustitucion sin `sessionExerciseId` habla de todo el
 * bloque y no tiene por que reescribir la sesion que se esta entrenando.
 *
 * `completed` **nunca**, ni con hueco: el historial es la base de todas las
 * decisiones posteriores. Lo mismo vale para las series YA registradas de la
 * sesion en curso, que no las toca nadie: cada `performed_set` guarda su propio
 * `exercise_slug`, asi que lo que se hizo sigue diciendo lo que se hizo y el
 * cambio solo alcanza a las series que quedan.
 */
function sesionSustituible(status: SessionStatus, señalaHueco: boolean): boolean {
  if (status === 'planned') return true;
  return status === 'in_progress' && señalaHueco;
}

/**
 * Cambia un ejercicio por otro en lo que queda de bloque.
 *
 * El alcance lo decide `sesionSustituible`. `planSnapshot` NO se toca: es lo que
 * se planifico, y el §61 exige que no cambie. Quien cuenta lo que pasó de
 * verdad son la decision registrada, el `substituted_from_slug` del hueco y el
 * `exercise_slug` de cada serie.
 */
async function aplicarSustitucion(
  ctx: WriteContext,
  fila: TrainingDecision,
): Promise<string | null> {
  const original = typeof fila.previousValue === 'string' ? fila.previousValue : null;
  const nuevo = typeof fila.newValue === 'string' ? fila.newValue : null;
  if (original === null || nuevo === null) return 'la propuesta no dice que cambiar por que';
  const sustituto = findExercise(nuevo);
  if (sustituto === undefined) return `el ejercicio «${nuevo}» no esta en el catalogo`;

  const bloque = await getActiveBlock(ctx.userId);
  if (!bloque) return 'no hay bloque activo';

  const entradas = fila.inputs as { sessionExerciseId?: string };
  const hueco = entradas.sessionExerciseId;
  const semanas = await listWeeks(bloque.id);

  /*
   * La maquina del ejercicio NUEVO, con la misma regla que al materializar la
   * semana: la primera del tipo que pide.
   *
   * Antes se heredaba la del original, y eso no es un detalle de limpieza: el
   * motor de cargas lee `equipmentId` para saber que pesos EXISTEN y de donde
   * sale el estado de progresion. Un press de banca sustituido por una maquina
   * seguía apuntando a la barra, asi que se redondeaba a discos que esa maquina
   * no tiene. No falla: propone un numero plausible que no se puede poner.
   */
  const gimnasio = await getDefaultGym(ctx.userId);
  const equipamiento = gimnasio === undefined ? [] : await listEquipment(gimnasio.id);
  const equipoDelSustituto =
    equipamiento.find((candidato) => candidato.kind === sustituto.equipmentKind)?.id ?? null;

  let cambiados = 0;
  let tecnicasReajustadas = 0;

  for (const semana of semanas) {
    for (const sesion of await listSessionsOfWeek(semana.id)) {
      if (!sesionSustituible(sesion.status, hueco !== undefined)) continue;

      const detalle = await getSessionDetail(sesion.id);
      if (detalle === null) continue;

      /*
       * La tecnica de cada hueco de ESTA sesion, en un mapa que se actualiza.
       *
       * El techo de drops del §11 es POR SESION, asi que hay que saber cuantos
       * hay ya en los demas huecos. Se lleva en un mapa y no recontando el
       * detalle porque el detalle se leyo una vez: una sustitucion sin hueco
       * concreto puede tocar dos ejercicios de la misma sesion, y el segundo
       * tiene que ver lo que decidio el primero.
       */
      const tecnicasPorHueco = new Map(
        detalle.exercises.map((item) => [item.sessionExercise.id, item.sessionExercise.technique]),
      );

      for (const item of detalle.exercises) {
        const ejercicio = item.sessionExercise;
        if (ejercicio.exerciseSlug !== original) continue;
        // Si la propuesta señalaba un hueco concreto, solo se toca ese.
        if (hueco !== undefined && ejercicio.id !== hueco) continue;

        /*
         * La tecnica del §11 se vuelve a DECIDIR, no se hereda.
         *
         * `technique` y los `set_type` se eligieron para el ejercicio de antes,
         * y `chooseTechnique` mira atributos que una sustitucion NO conserva:
         * el drop set esta reservado a aislamientos accesorios con demanda
         * tecnica <= 3, y el peso de «mismo rol» es 0,10 de 1,00, asi que un
         * candidato puede pasar el umbral cambiando `isCompound`. Heredarla
         * dejaba prescrito un drop set sobre un compuesto: el §11 lo prohibe y
         * nada avisaba, porque una prescripcion no se queja.
         *
         * `readiness` va en `null` A PROPOSITO, igual que al materializar la
         * semana. No es que no se sepa —sustituyendo en una sesion en curso el
         * check-in de hoy existe— sino que meterlo aqui lo dejaria fijado en una
         * prescripcion que puede entrenarse otro dia, y la segunda mitad de esa
         * regla ya la aplica `seriePermitidaHoy` en el momento de ejecutar.
         *
         * `timeConstrained` en `false` por lo mismo que alli: no hay ningun
         * sitio donde declarar prisa, y suponerla encenderia rest-pause y
         * myo-reps por defecto.
         */
        const decision = chooseTechnique({
          role: ejercicio.role,
          phase: bloque.phase,
          intent: semana.intent,
          // La misma cuenta que uso `materializeWeek` al decidir la tecnica la
          // primera vez, y sale del dominio para que no puedan divergir.
          accumulationWeeksLeft: accumulationWeeksLeftOf(
            bloque.phase,
            semana.weekIndex,
            bloque.plannedWeeks,
          ),
          readiness: null,
          technicalDemand: sustituto.technicalDemand,
          axialLoad: sustituto.axialLoad,
          isCompound: sustituto.isCompound,
          onStableMachine:
            sustituto.equipmentKind === 'machine_stack' || sustituto.equipmentKind === 'cable',
          timeConstrained: false,
          dropsYaEnLaSesion: dropsEnOtrosHuecos(tecnicasPorHueco, ejercicio.id),
        });

        await putRow(
          ctx,
          'sessionExercises',
          sessionExerciseSchema.parse({
            ...ejercicio,
            exerciseSlug: nuevo,
            equipmentId: equipoDelSustituto,
            substitutedFromSlug: original,
            technique: decision.technique,
            // El §11 exige justificacion registrada para incluir cualquier
            // tecnica. Misma regla que al materializar: `straight` no la lleva.
            notes: decision.technique === 'straight' ? null : decision.reasonCodes.join(' '),
          }),
        );
        tecnicasPorHueco.set(ejercicio.id, decision.technique);

        if (decision.technique !== ejercicio.technique) {
          await reescribirSeries(ctx, {
            sessionId: sesion.id,
            sessionExerciseId: ejercicio.id,
            prescripciones: item.prescriptions,
            tecnica: decision.technique,
          });
          tecnicasReajustadas += 1;
          logger.info('engine', 'TECHNIQUE_REDECIDED', 'Tecnica rehecha tras sustituir', {
            sessionExerciseId: ejercicio.id,
            antes: ejercicio.technique,
            ahora: decision.technique,
            motivos: decision.reasonCodes,
          });
        }

        // Las aproximaciones ya calculadas eran para el ejercicio de antes: sus
        // cargas salen de su e1RM y de su maquina. Se entierran para que se
        // vuelvan a generar; dejarlas seria pintar pesos de otro ejercicio como
        // si fueran el calentamiento de este.
        for (const aproximacion of item.warmups) {
          await softDeleteRow(ctx, 'warmupPrescriptions', aproximacion.id);
        }
        cambiados += 1;
      }
    }
  }

  if (cambiados === 0) return 'ese ejercicio ya no aparece en ninguna sesion pendiente';

  logger.info('engine', 'SUBSTITUTION_APPLIED', 'Ejercicio sustituido en el plan', {
    original,
    nuevo,
    huecos: cambiados,
    tecnicasReajustadas,
  });
  return null;
}

/** Drops ya colocados en los OTROS huecos de la sesion: el techo del §11 es por sesion. */
function dropsEnOtrosHuecos(
  tecnicas: ReadonlyMap<string, SessionExercise['technique']>,
  huecoActual: string,
): number {
  let drops = 0;
  for (const [id, tecnica] of tecnicas) {
    if (id !== huecoActual && tecnica === 'drop') drops += 1;
  }
  return drops;
}

/**
 * Alinea las filas de `set_prescriptions` con la tecnica recien decidida.
 *
 * EL VOLUMEN BASE NO SE MUEVE, y es la condicion para poder hacer esto.
 *
 * Las series base se recuperan de las filas que hay —`baseSetsOf`, inverso
 * declarado de `expandSets`— y se vuelven a expandir con la tecnica nueva, asi
 * que el numero base es el mismo antes y despues: lo que la rampa de la fase
 * pidio para ese hueco. Lo unico que se mueve del volumen contado es el 0,5 de
 * la fila `drop` que aparece o desaparece, que es exactamente lo que el §11
 * pretende. Inventarse el numero de series aqui seria saltarse las bandas del
 * §4.2 por la puerta de atras.
 *
 * LO YA REGISTRADO NO SE TOCA, NI PARA REESCRIBIR NI PARA ENTERRAR.
 *
 * Una serie hecha como `top` se hizo como `top`. Reescribir su tipo cambiaria
 * el volumen de algo que ya ocurrio, y enterrar su prescripcion dejaria el
 * `performed_set` apuntando a una fila que no existe. La consecuencia es que
 * tras sustituir a mitad el hueco puede quedar con una secuencia mezclada
 * —`top, backoff, effective, effective`—, y eso es lo correcto: describe lo
 * que paso, no lo que se habria planificado sabiendolo.
 */
async function reescribirSeries(
  ctx: WriteContext,
  entradas: {
    sessionId: string;
    sessionExerciseId: string;
    prescripciones: readonly SetPrescription[];
    tecnica: SessionExercise['technique'];
  },
): Promise<void> {
  const { sessionId, sessionExerciseId, prescripciones, tecnica } = entradas;
  const secuencia = expandSets(tecnica, baseSetsOf(prescripciones.map((fila) => fila.setType)));

  /*
   * Que indices de serie estan ya registrados.
   *
   * Se llega por el PARTE de la sesion y no preguntando por `sessionExerciseId`
   * a `performed_sets`: esa columna NO esta indexada en Dexie —lo estan `id`,
   * `userId`, `workoutLogId`, `exerciseSlug`, `performedAt` y `syncSeq`— y
   * pedirla lanza `SchemaError` en ejecucion, sin que el tipo se queje. Tampoco
   * vale filtrar por `exerciseSlug`: un hueco sustituido DOS veces tiene series
   * con el slug de cada etapa, y las de la primera se quedarian fuera, o sea
   * que enterrariamos la prescripcion de una serie hecha.
   *
   * Una sesion `planned` no tiene parte, asi que esto es una lectura indexada
   * que no devuelve nada y el conjunto sale vacio, que es lo correcto.
   */
  const parte = await getWorkoutBySession(sessionId);
  const registradas = new Set(
    (parte === undefined ? [] : await listPerformedSets(parte.id))
      .filter((set) => set.sessionExerciseId === sessionExerciseId)
      .map((set) => set.setIndex),
  );

  // De donde salen las repeticiones y el descanso de una fila NUEVA: de la
  // ultima que habia. Dentro de un hueco son uniformes —las escribe
  // `ensureWeekMaterialized` desde un solo `item.target`— y una fila nueva solo
  // aparece cuando la tecnica nueva pide mas que la vieja, tipicamente el
  // `drop` que se suma al final.
  const plantilla = prescripciones.at(-1);

  const filas = Math.max(secuencia.length, prescripciones.length);
  for (let indice = 1; indice <= filas; indice += 1) {
    const existente = prescripciones.find((fila) => fila.setIndex === indice);
    const tipo = secuencia[indice - 1];

    if (existente !== undefined && registradas.has(indice)) continue;

    if (existente !== undefined) {
      if (tipo === undefined) {
        await softDeleteRow(ctx, 'setPrescriptions', existente.id);
      } else if (existente.setType !== tipo) {
        await putRow(
          ctx,
          'setPrescriptions',
          setPrescriptionSchema.parse({ ...existente, setType: tipo }),
        );
      }
      continue;
    }

    if (tipo !== undefined && plantilla !== undefined) {
      await putNewRows(ctx, [
        {
          table: 'setPrescriptions',
          row: setPrescriptionSchema.parse({
            ...newSyncColumns(ctx),
            sessionExerciseId,
            setIndex: indice,
            setType: tipo,
            targetRepsMin: plantilla.targetRepsMin,
            targetRepsMax: plantilla.targetRepsMax,
            targetRir: plantilla.targetRir,
            targetLoadKg: null,
            restSeconds: plantilla.restSeconds,
          }),
        },
      ]);
    }
  }
}

// ---------------------------------------------------------------------------
// Prioridades musculares
// ---------------------------------------------------------------------------

/** Lleva al bloque activo las prioridades aprobadas (docs/TRAINING_ENGINE.md §4.4). */
async function aplicarPrioridades(
  ctx: WriteContext,
  fila: TrainingDecision,
): Promise<string | null> {
  const propuestas = musclePrioritiesSchema.safeParse(fila.newValue);
  if (!propuestas.success) return 'el valor propuesto no es un mapa de prioridades';

  const bloque = await getActiveBlock(ctx.userId);
  if (!bloque) return 'no hay bloque activo';

  // Se funden con las vigentes: una propuesta habla de los musculos que cambia,
  // no de los dieciocho.
  const combinadas = { ...bloque.musclePriorities, ...propuestas.data };
  await putRow(
    ctx,
    'trainingBlocks',
    trainingBlockSchema.parse({ ...bloque, musclePriorities: combinadas }),
  );

  logger.info('engine', 'PRIORITIES_APPLIED', 'Prioridades musculares actualizadas', {
    musculos: Object.keys(propuestas.data).length,
  });
  return null;
}

/**
 * Escalon 4 del estancamiento: sustituir el ejercicio por una variante.
 *
 * La propuesta original no nombra sustituto —el motor de estancamiento no sabe
 * que hay en el gimnasio—, asi que se elige aqui, con el equipamiento real, y
 * se reutiliza el mismo aplicador que una sustitucion pedida a mano.
 */
async function sustituirPorEstancamiento(
  ctx: WriteContext,
  fila: TrainingDecision,
  originalSlug: string,
): Promise<string | null> {
  const gimnasio = await getDefaultGym(ctx.userId);
  if (!gimnasio) return 'no hay gimnasio configurado';

  const equipamiento = await listEquipment(gimnasio.id);
  const candidatos = findSubstitutions(originalSlug, {
    availableKinds: [...new Set(equipamiento.map((equipo) => equipo.kind))],
  });

  const elegido = candidatos[0];
  if (elegido === undefined) {
    return 'no hay ninguna alternativa disponible con este equipamiento';
  }

  return aplicarSustitucion(ctx, {
    ...fila,
    previousValue: originalSlug,
    newValue: elegido.slug,
  });
}
